import { ActionType } from "../../constants/ActionTypes";

// --- Page content request ownership --------------------------------------------
// getPageContent/getPageContentHistory share one content stream and getPageHistory
// owns the history stream, but all of them write into this one global slice, so a
// superseded response would otherwise overwrite the context the user switched
// to. The reducer therefore tracks the ACTIVE request of each stream in its own
// state (contentRequest/historyRequest) plus contentScope, the last begun
// content context. It is per-store state — never a module-global "latest
// request" variable — so two independent stores never block each other.

const normalizeIdentifier = value => (value === null || value === undefined ? null : String(value));

// The context portion of a request scope: a response may only mutate state
// issued for this exact tenant/project/page/language. The requested contentId
// (revision/history target) is deliberately not part of it — a revision load of
// the current context is a normal reload of the same content stream.
const isSameContentScopeContext = (previousScope, nextScope) => {
	const previous = previousScope || {};
	const next = nextScope || {};
	return (
		normalizeIdentifier(previous.companyId) === normalizeIdentifier(next.companyId) &&
		normalizeIdentifier(previous.headerProjectId) === normalizeIdentifier(next.headerProjectId) &&
		normalizeIdentifier(previous.selectedProjectId) === normalizeIdentifier(next.selectedProjectId) &&
		normalizeIdentifier(previous.pageId) === normalizeIdentifier(next.pageId) &&
		normalizeIdentifier(previous.languageId) === normalizeIdentifier(next.languageId)
	);
};

// A terminal (SUCCESS/ERROR) is accepted only from the request that currently
// owns its stream. A tracked terminal (meta.requestId) must match the active
// owner exactly — with no active owner it is a superseded, duplicate or
// invalidated completion and is rejected. A legacy terminal without meta may
// never bypass an active tracked request; with no tracked request active it
// keeps its exact legacy behavior.
const isOwnedTerminal = (state, requestField, action) => {
	const owner = state[requestField];
	if (action.meta && action.meta.requestId !== undefined) {
		return !!owner && owner.requestId === action.meta.requestId;
	}
	return !owner;
};

const beginTrackedRequest = (state, requestField, action) => {
	const meta = action.meta;
	if (!meta || meta.requestId === undefined) {
		return state;
	}
	return {
		...state,
		[requestField]: { requestId: meta.requestId, scope: meta.scope },
	};
};

// Dropping the tracked owners of a moved-on context also ends their loading
// flags: their responses are rejected, so the spinners must not stay armed.
const invalidateTrackedRequests = state => ({
	...state,
	contentRequest: null,
	historyRequest: null,
	isContentloading: false,
	isHistoryloading: false,
});

export default function pagesReducer(state = {}, action = {}) {
	switch (action.type) {
		case ActionType.GET_PAGES_BEGIN:
			return {
				...state,
				loading: true,
				data: [],
			};
		case ActionType.GET_PAGES_ERROR:
			return {
				...state,
				loading: false,
			};
		case ActionType.GET_PAGES_SUCCESS:
			return {
				...state,
				loading: false,
				data: action.payload,
			};

		case ActionType.GET_PAGE_CONTENTS_BEGIN:
			return {
				...state,
				isContentsloading: true,
				pageContents: [],
				pageHistory: [],
			};
		case ActionType.GET_PAGE_CONTENTS_ERROR:
			return {
				...state,
				isContentsloading: false,
			};
		case ActionType.GET_PAGE_CONTENTS_SUCCESS:
			return {
				...state,
				isContentsloading: false,
				pageContents: action.payload,
			};
		case ActionType.GET_PAGE_CONTENT_BEGIN:
		case ActionType.GET_PAGE_CONTENT_AND_RESET_HISTORY_BEGIN: {
			const resetHistory = action.type === ActionType.GET_PAGE_CONTENT_AND_RESET_HISTORY_BEGIN;
			const tracked = action.meta && action.meta.requestId !== undefined ? action.meta : null;
			let ownership = state;
			if (tracked) {
				// A content request for a changed source context invalidates the history
				// ownership immediately — even before the new context's own history
				// request starts — so an in-flight history response of the abandoned
				// context can never land as the visible version list. A same-context
				// reload/revision begin keeps the visible history and its ownership,
				// exactly as before.
				if (state.contentScope && !isSameContentScopeContext(state.contentScope, tracked.scope)) {
					ownership = {
						...ownership,
						historyRequest: null,
					};
				}
				ownership = {
					...ownership,
					contentScope: tracked.scope,
					contentRequest: { requestId: tracked.requestId, scope: tracked.scope },
				};
			}
			return {
				...ownership,
				isContentloading: true,
				pageContent: [],
				...(resetHistory ? { pageHistory: [] } : {}),
			};
		}
		case ActionType.GET_PAGE_CONTENT_ERROR:
			if (!isOwnedTerminal(state, "contentRequest", action)) {
				return state;
			}
			return {
				...state,
				contentRequest: null,
				isContentloading: false,
			};
		case ActionType.GET_PAGE_CONTENT_SUCCESS:
			if (!isOwnedTerminal(state, "contentRequest", action)) {
				return state;
			}
			return {
				...state,
				contentRequest: null,
				isContentloading: false,
				pageContent: action.payload,
			};
		case ActionType.GET_PAGE_HISTORY_BEGIN:
			return {
				...beginTrackedRequest(state, "historyRequest", action),
				isHistoryloading: true,
				pageHistory: [],
			};
		case ActionType.GET_PAGE_HISTORY_ERROR:
			if (!isOwnedTerminal(state, "historyRequest", action)) {
				return state;
			}
			return {
				...state,
				historyRequest: null,
				isHistoryloading: false,
			};
		case ActionType.GET_PAGE_HISTORY_SUCCESS:
			if (!isOwnedTerminal(state, "historyRequest", action)) {
				return state;
			}
			return {
				...state,
				historyRequest: null,
				isHistoryloading: false,
				pageHistory: action.payload,
			};
		case ActionType.GET_PAGE_URLS_BEGIN:
			return {
				...state,
				isUrlsloading: true,
				pageUrls: [],
			};
		case ActionType.GET_PAGE_URLS_ERROR:
			return {
				...state,
				isUrlsloading: false,
			};
		case ActionType.GET_PAGE_URLS_SUCCESS:
			return {
				...state,
				isUrlsloading: false,
				pageUrls: action.payload,
			};

		case ActionType.ADD_PAGE_BEGIN:
			return {
				...state,
				saving: true,
			};
		case ActionType.ADD_PAGE_ERROR:
		case ActionType.ADD_PAGE_SUCCESS:
			return {
				...state,
				saving: false,
			};
		case ActionType.EDIT_PAGE_BEGIN:
			return {
				...state,
				isPageloading: true,
			};
		case ActionType.EDIT_PAGE_ERROR:
		case ActionType.EDIT_PAGE_SUCCESS:
			return {
				...state,
				isPageloading: false,
			};
		case ActionType.SAVE_PAGE_CONTENT_BEGIN:
			return {
				...state,
				isContentSaving: true,
			};
		case ActionType.SAVE_PAGE_CONTENT_ERROR:
		case ActionType.SAVE_PAGE_CONTENT_SUCCESS:
			return {
				...state,
				isContentSaving: false,
			};

		case ActionType.DELETE_PAGE_BEGIN:
			return {
				...state,
				deleting: true,
			};
		case ActionType.DELETE_PAGE_ERROR:
		case ActionType.DELETE_PAGE_SUCCESS:
			return {
				...state,
				deleting: false,
			};

		// Lifecycle: the customization header cleanup resets the selected project on
		// unmount and the project selection can change (deep link, project switch).
		// The tracked page requests were issued for the previously selected project,
		// so their responses may no longer mutate this slice. A refresh of the SAME
		// project (identical id, compared normalized) keeps every valid in-flight
		// request running; GET_PROJECT_BEGIN alone never invalidates anything.
		case ActionType.RESET_SELECTED_PROJECT:
			return invalidateTrackedRequests(state);
		case ActionType.GET_PROJECT_SUCCESS: {
			const nextProjectId = normalizeIdentifier(action.payload && action.payload.id);
			const trackedOwners = [state.contentRequest, state.historyRequest].filter(Boolean);
			const movedOn = trackedOwners.some(
				owner =>
					normalizeIdentifier(owner.scope && owner.scope.selectedProjectId) !== null &&
					normalizeIdentifier(owner.scope && owner.scope.selectedProjectId) !== nextProjectId,
			);
			if (!movedOn) {
				return state;
			}
			return invalidateTrackedRequests(state);
		}
		default:
			return state;
	}
}
